import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { User, Loader2, BookOpen, Calendar, Clock, ChevronDown, ChevronRight, Play, Trophy, Users } from 'lucide-react';
import { TeamPlayerCard } from '@/components/homepage/TeamPlayerCard';
import { TeamPlayerBreadcrumb } from '@/components/homepage/TeamPlayerBreadcrumb';
import { supabase } from '@/integrations/supabase/client';
import { usePlayerPrograms } from '@/hooks/usePlayerPrograms';
import { useActivities } from '@/hooks/useActivities';
import { useState, useEffect } from 'react';
import { Database } from '@/integrations/supabase/types';
import { transformPlayerData } from '@/utils/playerTransform';

type TeamRow = Database['public']['Tables']['teams']['Row'];
type PlayerRow = Database['public']['Tables']['players']['Row'];

const TeamPlayerActivity = () => {
  const { playerId } = useParams<{ playerId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<TeamRow | null>(null);
  const [player, setPlayer] = useState<PlayerRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedProgram, setExpandedProgram] = useState<string | null>(null);
  const { playerPrograms, loading: programsLoading } = usePlayerPrograms(playerId);
  const { data: activities, isLoading: activitiesLoading } = useActivities(
    expandedProgram || undefined,
    !!expandedProgram
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!playerId) return;
      
      setLoading(true);
      
      // Fetch player data
      const { data: playerData } = await supabase
        .from('players')
        .select(`
          *,
          profile:profiles(*),
          team:teams(
            *,
            clubs:clubs(id, name)
          )
        `)
        .eq('id', playerId)
        .single();
      
      setPlayer(playerData);
      setTeam(playerData?.team);
      setLoading(false);
    };

    fetchData();
  }, [playerId]);

  if (!playerId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground">Player not found</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Navigation />
      
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <TeamPlayerBreadcrumb 
          team={team} 
          playerName={player ? `${(player as PlayerRow & { profile?: { first_name?: string; last_name?: string } }).profile?.first_name || ''} ${(player as PlayerRow & { profile?: { first_name?: string; last_name?: string } }).profile?.last_name || ''}`.trim() : undefined}
        />

        {/* Player Information */}
        {loading ? (
          <Card className="mb-8">
            <CardContent className="py-8">
              <div className="flex items-center gap-3 justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
                <span className="text-muted-foreground">Loading player data...</span>
              </div>
            </CardContent>
          </Card>
        ) : !player ? (
          <Card className="mb-8">
            <CardContent className="text-center py-8 text-muted-foreground">
              <User className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Player Not Found</p>
              <p>The requested player could not be found.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="mb-8 space-y-6">
            <TeamPlayerCard player={transformPlayerData(player)} teamId={team?.id} />
            
            {/* Player Programs */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Training Programs
                  </CardTitle>
                  <Button variant="outline" size="sm">
                    Book a Session
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {programsLoading ? (
                  <div className="flex items-center gap-3 justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span className="text-muted-foreground">Loading programs...</span>
                  </div>
                ) : playerPrograms.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No Programs Assigned</p>
                    <p>This player is not currently assigned to any training programs.</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2">
                    {playerPrograms.map((playerProgram) => (
                      <Card 
                        key={playerProgram.id} 
                        className="hover:shadow-lg transition-all duration-200 cursor-pointer hover:scale-[1.02] border-0 bg-white shadow-md"
                        onClick={() => navigate(`/player/${playerId}/programs/${playerProgram.program?.id}`)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between mb-3">
                            <CardTitle className="text-lg font-bold">
                              {playerProgram.program?.title}
                            </CardTitle>
                            <Badge 
                              variant="outline" 
                              className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                            >
                              {playerProgram.program?.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {playerProgram.program?.description || 'Complete the assigned training activities and track your progress'}
                          </p>
                        </CardHeader>
                        
                        <CardContent className="pt-0">
                          <div className="space-y-3">
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                Created by Coach
                              </div>
                              <div className="flex items-center gap-1">
                                <Trophy className="w-3 h-3" />
                                Training Program
                              </div>
                            </div>
                            
                            <div className="flex items-center justify-between text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Users className="w-3 h-3" />
                                Individual
                              </div>
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(playerProgram.assigned_at).toLocaleDateString()}
                              </div>
                            </div>
                            
                            
                            <Button 
                              variant="outline" 
                              className="w-full mt-3 text-primary border-primary hover:bg-primary hover:text-primary-foreground"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/player/${playerId}/programs/${playerProgram.program?.id}`);
                              }}
                            >
                              View Details
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

      </div>
    </div>
  );
};

export default TeamPlayerActivity;